import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, X, Target, Repeat, Package, LifeBuoy, Wallet, Gauge, Eye } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { TopographicPattern } from '../components/TopographicPattern';
type StepSection = {
  label: string;
  marker: 'dot' | 'check' | 'x' | 'arrow';
  content?: string;
  list?: string[];
};
type Step = {
  num: string;
  title: string;
  anchor: string;
  summary: string;
  preview: string;
  duration: string;
  required: string;
  hasCta?: boolean;
  sections: StepSection[];
};
export function HowWeFixIt() {
  const location = useLocation();
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const steps: Step[] = [{
    num: '1',
    title: 'Honest Conversation',
    anchor: 'honest-conversation',
    summary: 'We pressure-test what you are seeing and decide whether there is a real issue or opportunity worth diagnosing.',
    preview: 'Clarity + next step',
    duration: 'Free · 45 minutes',
    required: 'Required start',
    hasCta: true,
    sections: [{
      label: 'What we do',
      marker: 'check',
      list: ['pressure-test what changed as the business grew', 'clarify where revenue may be getting stuck, lost, delayed, or missed', 'separate structural issues from noise or normal friction', 'make the next right step clearer']
    }, {
      label: 'Possible outcomes',
      marker: 'arrow',
      list: ['You are fine. Keep pushing.', 'Something is real here, but it is not the right timing yet.', 'A Revenue Journey Diagnostic is warranted.']
    }, {
      label: 'Done when',
      marker: 'dot',
      content: 'There is a clear next-step decision — including "keep pushing" if that is the truth.'
    }]
  }, {
    num: '2',
    title: 'Revenue Journey Diagnostic',
    anchor: 'revenue-journey-diagnostic',
    summary: 'If the issue is deeper, we diagnose where the journey no longer fits reality and identify the highest-leverage point to address first.',
    preview: 'Problem definition + recommendation',
    duration: 'Paid · time-boxed',
    required: 'Step 2',
    sections: [{
      label: 'What gets looked at',
      marker: 'dot',
      list: ['who is entering the system', 'how qualification and fit are being determined', 'where routing and handoffs break down', 'where old assumptions are still shaping customer movement', 'where promises drift from what delivery can support', 'where churn is being created upstream', 'how expansion opportunities are surfaced or missed', 'whether pricing or packaging still fits the way customers actually buy and move', 'where workflow, reporting, or AI could help once the logic is clear']
    }, {
      label: 'What you get',
      marker: 'check',
      list: ['a clear problem definition', 'evidence and priority areas', 'a recommendation on what to fix first', 'a clearer sense of what not to do']
    }, {
      label: 'What this is not',
      marker: 'x',
      list: ['a full-company audit', 'a tool shopping exercise', 'a giant backlog of improvements', 'implementation disguised as diagnosis']
    }, {
      label: 'Done when',
      marker: 'dot',
      content: 'You have a clear read on what changed, where the mismatch is, and what part of the journey is worth redesigning first.'
    }]
  }, {
    num: '3',
    title: 'Revenue Journey Redesign',
    anchor: 'revenue-journey-redesign',
    summary: 'If fixing it together makes sense, we redesign the right part in the right order so the team can run it with less friction, less waste, and more captured upside.',
    preview: 'Redesign + handoff',
    duration: 'Paid · scoped engagement',
    required: 'Step 3',
    sections: [{
      label: 'What redesign may include',
      marker: 'check',
      list: ['clarifying qualification rules', 'tightening handoffs', 'redefining stages or statuses', 'improving promise-to-delivery alignment', 'surfacing expansion pathways', 'redesigning workflow around real exceptions', 'updating pricing or packaging that no longer fits', 'improving visibility so the real signal is easier to trust', 'hardening the process before AI or automation gets added']
    }, {
      label: 'What done looks like',
      marker: 'check',
      list: ['decisions resolve lower', 'the team needs less interpretation to move', 'handoffs hold up better under real load', 'fewer issues bounce back through leadership', 'the signal is easier to trust', 'customers hit fewer dead ends', 'more of the right revenue converts, sticks, and expands', 'the redesigned part can run without you sitting in the middle']
    }, {
      label: 'What this is not',
      marker: 'x',
      list: ['a giant transformation program', 'general business consulting', 'a framework install', 'fractional COO stewardship', 'generic AI consulting', 'a dev shop', 'an indefinite operating dependency']
    }]
  }];
  useEffect(() => {
    const hash = location.hash.replace('#', '').toLowerCase();
    if (!hash) return;
    const hashToIndex: Record<string, number> = {
      'step-1': 0,
      'honest-conversation': 0,
      'step-2': 1,
      diagnostic: 1,
      'revenue-journey-diagnostic': 1,
      'constraint-diagnostic': 1,
      'operating-diagnostic': 1,
      'step-3': 2,
      redesign: 2,
      'revenue-journey-redesign': 2,
      'focused-redesign': 2,
      intervention: 2
    };
    const idx = hashToIndex[hash];
    if (idx === undefined) return;
    setExpandedStep(idx);
    setTimeout(() => {
      const el = document.getElementById(`step-${idx + 1}`);
      if (!el) return;
      const offset = 96;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }, 100);
  }, [location.hash]);
  return <main className="w-full" data-id="element-522">
      {/* Hero */}
      <section className="relative py-24 px-6 bg-paper pt-32 overflow-hidden" data-id="element-523">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-524">
          <BlueprintGrid data-id="element-525" />
        </div>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-526">
          <motion.p variants={fadeIn} className="eyebrow mb-4 text-seaglass" data-id="element-527">
            How We Fix It
          </motion.p>

          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl font-serif font-semibold mb-6 leading-tight" data-id="element-528">
            <span style={{
            color: '#5A6B7C'
          }} data-id="element-529">
              A simple path
            </span>{' '}
            <span className="text-navy" data-id="element-530">to the real bottleneck.</span>
          </motion.h1>

          <motion.div variants={fadeIn} className="text-lg text-text max-w-3xl mb-8 space-y-4" data-id="element-531">
            <p data-id="element-532">Most companies do not need a full-company reset.</p>
            <p data-id="element-533">
              Revenue problems rarely stay contained inside one department. That
              is why we start wide — across demand, conversion, delivery,
              retention, and expansion — then go deep where the journey no
              longer fits reality.
            </p>
            <p data-id="element-534">
              The goal is a clearer read on what changed, where revenue is
              getting stuck or missed, and what part of the journey is worth
              redesigning first.
            </p>
            <p className="font-medium text-navy" data-id="element-535">That is the work.</p>
          </motion.div>

          <motion.div variants={fadeIn} className="flex flex-col" data-id="element-536">
            <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300 w-fit" data-id="element-537">
              <span data-id="element-538">Book an Honest Conversation</span>
              <ArrowRight className="w-5 h-5" data-id="element-539" />
            </Link>
            <p className="text-sm text-text-muted mt-2" data-id="element-540">
              45 minutes. No pitch. Just clarity.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Sequence Intro */}
      <section className="py-24 px-6 border-t hairline-top bg-paper-dark" data-id="element-541">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-542">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-543">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-544">
              Redesign is a sequence, not a menu.
            </h2>
            <div className="text-lg text-text max-w-3xl space-y-4" data-id="element-545">
              <p data-id="element-546">Each step has a clear output and a stopping point.</p>
              <p data-id="element-547">
                We only move to the next step when it is worth the time and
                money.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* What we look at */}
      <section id="what-we-look-at" className="py-24 px-6 bg-paper" data-id="element-548">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-549">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-550">
            <p className="eyebrow mb-4 text-seaglass" data-id="element-551">What we look at</p>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-6 leading-tight" data-id="element-552">
              <span style={{
              color: '#5A6B7C'
            }} data-id="element-553">
                We start wide.
              </span>{' '}
              <span className="text-navy" data-id="element-554">Then go deep.</span>
            </h2>
            <p className="text-lg text-text max-w-3xl" data-id="element-555">
              We take a fast pass across the few flows that make the business
              work end to end, then go deeper where the drag is actually being
              created.
            </p>
          </motion.div>

          <motion.div variants={fadeIn} data-id="element-556">
            <div data-id="element-557">
              {[{
              name: 'Lead-to-Revenue',
              Icon: Target,
              assessment: 'How attention becomes qualified demand, committed deals, and booked money.'
            }, {
              name: 'Offer-to-Economics Loop',
              Icon: Repeat,
              assessment: 'How learning becomes a tighter offer: clearer boundaries, smarter pricing and packaging, and less complexity over time.'
            }, {
              name: 'Fulfillment / Delivery',
              Icon: Package,
              assessment: 'How what is sold gets produced, delivered, and made real.'
            }, {
              name: 'Service / Support',
              Icon: LifeBuoy,
              assessment: 'How issues get resolved, customers stay happy, and revenue expands instead of churns.'
            }, {
              name: 'Cash & Commitments',
              Icon: Wallet,
              assessment: 'How money gets invoiced and collected, and obligations stay clean and current.'
            }, {
              name: 'Capacity & Resourcing',
              Icon: Gauge,
              assessment: 'How time, people, inventory, and throughput get allocated so the business can keep its promises.'
            }, {
              name: 'Systems & Truth',
              Icon: Eye,
              assessment: 'How information moves, definitions stay consistent, and leaders can trust what they are seeing.'
            }].map((flow, i) => <div key={i} className="grid md:grid-cols-[2fr_3fr] gap-3 md:gap-12 py-6 border-t border-seaglass/20 first:border-0" data-id="element-558">
                  <div className="flex items-start gap-3" data-id="element-559">
                    <flow.Icon className="text-seaglass flex-shrink-0 mt-1" strokeWidth={1.25} size={24} aria-hidden="true" data-id="element-560" />
                    <h3 className="font-serif font-semibold text-navy text-xl leading-snug" data-id="element-561">
                      {flow.name}
                    </h3>
                  </div>
                  <p className="text-base text-text leading-relaxed" data-id="element-562">
                    {flow.assessment}
                  </p>
                </div>)}
            </div>
          </motion.div>

          <motion.div variants={fadeIn} className="mt-12" data-id="element-563">
            <p className="text-body text-navy font-semibold" data-id="element-564">
              We scan all seven. Then we concentrate where the drag is actually
              being created.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Three Steps */}
      <section id="three-steps" className="relative py-24 px-6 bg-[#8BA89F26] grain border-y border-seaglass/20 overflow-hidden" data-id="element-565">
        <TopographicPattern color="sage" opacity={0.06} data-id="element-566" />

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-567">
          <div className="space-y-0" data-id="element-568">
            {steps.map((step, i) => <motion.div key={step.anchor} id={`step-${step.num}`} variants={fadeIn} className="border-b border-seaglass/20 last:border-0" data-id="element-569">
                <button onClick={() => setExpandedStep(expandedStep === i ? null : i)} className="w-full py-6 flex items-start justify-between text-left group" data-id="element-570">
                  <div className="flex-1 pr-6" data-id="element-571">
                    <div className="flex items-start justify-between mb-2" data-id="element-572">
                      <h3 className="text-2xl font-serif font-semibold text-navy" data-id="element-573">
                        {step.num}) {step.title}
                      </h3>
                      <div className="text-right ml-4 flex-shrink-0" data-id="element-574">
                        <div className="text-xs font-medium text-seaglass uppercase tracking-wider" data-id="element-575">
                          {step.required}
                        </div>
                        <div className="text-xs text-text-muted" data-id="element-576">
                          {step.duration}
                        </div>
                      </div>
                    </div>
                    <p className="text-base text-text mb-1" data-id="element-577">{step.summary}</p>
                    <p className="text-xs font-mono text-seaglass uppercase tracking-wider" data-id="element-578">
                      {step.preview}
                    </p>
                  </div>

                  <ChevronDown className={`w-5 h-5 text-seaglass flex-shrink-0 transition-transform duration-300 mt-1 ${expandedStep === i ? 'rotate-180' : ''}`} data-id="element-579" />
                </button>

                {expandedStep === i && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="pb-8" data-id="element-580">
                    <div className="space-y-6" data-id="element-581">
                      {step.sections.map((section, j) => <div key={j} className="py-4 border-t border-seaglass/20 first:border-0" data-id="element-582">
                          <p className="text-base font-semibold text-navy mb-3" data-id="element-583">
                            {section.label}
                          </p>

                          {section.content && <p className="text-base text-text leading-relaxed" data-id="element-584">
                              {section.content}
                            </p>}

                          {section.list && <ul className="space-y-2" data-id="element-585">
                              {section.list.map((item, k) => <li key={k} className="flex items-start space-x-2 text-base text-text" data-id="element-586">
                                  {section.marker === 'check' ? <Check className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-587" /> : section.marker === 'x' ? <X className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-588" /> : section.marker === 'arrow' ? <ArrowRight className="w-3.5 h-3.5 text-seaglass flex-shrink-0 mt-1.5" data-id="element-589" /> : <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-590" />}
                                  <span data-id="element-591">{item}</span>
                                </li>)}
                            </ul>}
                        </div>)}

                      {step.hasCta && <div className="pt-4 border-t border-seaglass/20" data-id="element-592">
                          <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-593">
                            <span data-id="element-594">Book an Honest Conversation</span>
                            <ArrowRight className="w-5 h-5" data-id="element-595" />
                          </Link>
                        </div>}
                    </div>
                  </motion.div>}
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* Why This Works */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-596">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-597">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-598">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-599">
              We do not start with the tool.
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6 text-body" data-id="element-600">
            <p className="text-text" data-id="element-601">
              We do not start by prescribing software, dashboards, frameworks,
              AI, or a new management layer.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-602">
              We start by finding the real failure point.
            </p>
            <p className="text-text" data-id="element-603">
              That matters because the loudest symptom is often downstream from
              the real cause. Until the cause is clear, most fixes just add
              cost, complexity, or false progress.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-604">
              Technology follows clarity. Redesign follows diagnosis.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* The point */}
      <section className="py-24 px-6 bg-paper" data-id="element-605">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-606">
          <motion.div variants={fadeIn} className="border-l-2 border-seaglass pl-8 py-2" data-id="element-607">
            <p className="eyebrow mb-3 text-seaglass" data-id="element-608">The point</p>
            <p className="text-body text-navy font-medium mb-3" data-id="element-609">
              The point is not to create a bigger backlog of things to fix.
            </p>
            <p className="text-body text-navy font-medium" data-id="element-610">
              The point is to identify the next real bottleneck, the dead end,
              or the growth lever most worth pulling next.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 px-6 bg-navy grain overflow-hidden" data-id="element-611">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-612">
          <BlueprintGrid data-id="element-613" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="relative max-w-3xl mx-auto text-center z-10" data-id="element-614">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-6 leading-tight" data-id="element-615">
            <span style={{
            color: '#BBBFC4'
          }} data-id="element-616">
              You do not need a bigger plan first.
            </span>{' '}
            <span className="text-white" data-id="element-617">You need a clearer read.</span>
          </h2>
          <p className="text-lg text-white/80 mb-10" data-id="element-618">
            Start with the conversation. If the issue is real, we will make the
            next step obvious.
          </p>
          <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300 group" data-id="element-619">
            <span data-id="element-620">Book an Honest Conversation</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" data-id="element-621" />
          </Link>
          <p className="text-sm text-white/55 mt-4" data-id="element-622">
            45 minutes. No pitch. Just clarity.
          </p>
        </motion.div>
      </section>
    </main>;
}